import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import ReactApexChart from "react-apexcharts";
import { useAppServices } from "../../../hook/services";
import SubAccountDetail from "./SubAccountDetail";

const HOURS = ["12 AM","1 AM","2 AM","3 AM","4 AM","5 AM","6 AM","7 AM","8 AM","9 AM","10 AM","11 AM","12 PM","1 PM","2 PM","3 PM","4 PM","5 PM","6 PM","7 PM","8 PM","9 PM","10 PM","11 PM"];
const DAYS  = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
// Map JS getDay() (0=Sun…6=Sat) to a Monday-first column index (0=Mon…6=Sun).
const mondayIndex = (jsDay) => (jsDay + 6) % 7;
const HEAT_COLORS = ["#eef2ff","#c7d7fc","#93b4f9","#5b8ef5","#2563eb","#1e3a8a"];
const HEAT_LABELS = ["None","Low","Medium","High","Very High","Peak"];

const FREQ_STYLE = {
  "Ghosting":     { bg: "#fef2f2", color: "#dc2626", dot: "#ef4444" },
  "Low Activity": { bg: "#fef9c3", color: "#92400e", dot: "#f59e0b" },
  "Active":       { bg: "#f0fdf4", color: "#15803d", dot: "#22c55e" },
  "Dormant":      { bg: "#fef2f2", color: "#dc2626", dot: "#ef4444" },
  "Highly Active":     { bg: "#f0fdf4", color: "#15803d", dot: "#16a34a" },
  "Moderately Active": { bg: "#f0fdf4", color: "#15803d", dot: "#22c55e" },
};

const PALETTE = ["#6366f1","#22c55e","#3b82f6","#f59e0b","#06b6d4","#8b5cf6","#ef4444","#10b981","#0ea5e9","#f97316"];
const colorFor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const initialsOf = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const sumTimesheetSeconds = (timesheet) => {
  if (!timesheet || typeof timesheet !== "object") return 0;
  return Object.values(timesheet).reduce((a, v) => a + (Number(v) || 0), 0);
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
};

const formatDateTime = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const daysBetween = (from, to = new Date()) => {
  if (!from) return Infinity;
  const dt = new Date(from);
  if (isNaN(dt)) return Infinity;
  return Math.floor((to - dt) / 86400000);
};

const frequencyLabel = (loginDayCount) => {
  if (loginDayCount >= 26) return "Highly Active";
  if (loginDayCount >= 15) return "Moderately Active";
  if (loginDayCount >= 6)  return "Low Activity";
  return "Ghosting";
};

const heatLevelFromPct = (pct) => {
  if (pct <= 0)  return 0;
  if (pct < 10)  return 1;
  if (pct < 30)  return 2;
  if (pct < 55)  return 3;
  if (pct < 80)  return 4;
  return 5;
};

const LoginActivity = () => {
  const AppService = useAppServices();
  const { companyId } = useParams();

  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loginActivity, setLoginActivity] = useState([]);
  const [feedback, setFeedback] = useState([]);

  const [activeTab, setActiveTab]     = useState("overview");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [subSearch, setSubSearch]     = useState("");
  const [userSearch, setUserSearch]   = useState("");
  const [togglingId, setTogglingId]   = useState(null);
  const [trendRange, setTrendRange]   = useState("Day");
  const [heatView, setHeatView]       = useState("week");
  const [tooltip, setTooltip]         = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [metric, setMetric]           = useState("Active Users");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { response } = await AppService.webhook.getloginactivity_data({
          query: `company_id=${companyId}`,
        });
        if (!cancelled && response?.success) {
          setLocations(response.locations || []);
          setUsers(response.users || []);
          setLoginActivity(response.loginActivity || []);
          setFeedback(response.feedback || []);
        }
      } catch (e) {
        console.error("Login activity fetch failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  /* ── Lookups & per-record aggregations ─────────────────────────── */
  const userById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => { if (u?.id) m.set(String(u.id), u); });
    return m;
  }, [users]);

  const locationById = useMemo(() => {
    const m = new Map();
    locations.forEach((l) => { if (l?.id) m.set(String(l.id), l); });
    return m;
  }, [locations]);

  // Per-user: last login + distinct login days (last 30) + login count
  const userActivity = useMemo(() => {
    const map = new Map();
    const since = Date.now() - 30 * 86400000;
    loginActivity.forEach((la) => {
      const uid = la.userId ? String(la.userId) : null;
      if (!uid) return;
      const ts = la.createdAt ? new Date(la.createdAt).getTime() : 0;
      const cur = map.get(uid) || { lastLogin: null, loginDays: new Set(), totalLogins: 0, locationId: la.LocationId || null };
      cur.totalLogins += 1;
      if (!cur.lastLogin || ts > new Date(cur.lastLogin).getTime()) cur.lastLogin = la.createdAt;
      if (ts >= since) {
        const d = new Date(ts);
        cur.loginDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      }
      if (!cur.locationId && la.LocationId) cur.locationId = la.LocationId;
      map.set(uid, cur);
    });
    return map;
  }, [loginActivity]);

  // Per-location: last login + active user count + recent login count
  const locationActivity = useMemo(() => {
    const map = new Map();
    const since7  = Date.now() - 7  * 86400000;
    const since30 = Date.now() - 30 * 86400000;
    loginActivity.forEach((la) => {
      const lid = la.LocationId ? String(la.LocationId) : null;
      if (!lid) return;
      const ts = la.createdAt ? new Date(la.createdAt).getTime() : 0;
      const cur = map.get(lid) || {
        lastLogin: null,
        activeUsers7d: new Set(),
        activeUsers30d: new Set(),
        totalLogins: 0,
      };
      cur.totalLogins += 1;
      if (!cur.lastLogin || ts > new Date(cur.lastLogin).getTime()) cur.lastLogin = la.createdAt;
      if (la.userId) {
        if (ts >= since7)  cur.activeUsers7d.add(String(la.userId));
        if (ts >= since30) cur.activeUsers30d.add(String(la.userId));
      }
      map.set(lid, cur);
    });
    return map;
  }, [loginActivity]);

  /* ── Overview KPIs ─────────────────────────────────────────────── */
  const overviewStats = useMemo(() => {
    const totalAccounts = locations.length;
    let active = 0, atRisk = 0, dormant = 0;
    locations.forEach((l) => {
      const act = locationActivity.get(String(l.id));
      const days = act?.lastLogin ? daysBetween(act.lastLogin) : Infinity;
      if (days <= 7)        active  += 1;
      else if (days <= 30)  atRisk  += 1;
      else                  dormant += 1;
    });
    return { totalAccounts, active, atRisk, dormant };
  }, [locations, locationActivity]);

  /* ── Frequency buckets (accounts) ──────────────────────────────── */
  const freqBars = useMemo(() => {
    const buckets = { "Highly Active": 0, "Moderately Active": 0, "Low Activity": 0, "Ghosting": 0 };
    const since = Date.now() - 30 * 86400000;
    locations.forEach((l) => {
      const days = new Set();
      loginActivity.forEach((la) => {
        if (String(la.LocationId) !== String(l.id)) return;
        const ts = la.createdAt ? new Date(la.createdAt).getTime() : 0;
        if (ts < since) return;
        const d = new Date(ts);
        days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      });
      buckets[frequencyLabel(days.size)] += 1;
    });
    return [
      { label: "Highly Active",     sub: "26+ days",          count: buckets["Highly Active"],     color: "#16a34a" },
      { label: "Moderately Active", sub: "15-25 days",        count: buckets["Moderately Active"], color: "#22c55e" },
      { label: "Low Activity",      sub: "6-14 days",         count: buckets["Low Activity"],      color: "#f59e0b" },
      { label: "Ghosting",          sub: "fewer than 6 days", count: buckets["Ghosting"],          color: "#ef4444" },
    ];
  }, [locations, loginActivity]);
  const maxFreq = Math.max(...freqBars.map((b) => b.count), 1);

  /* ── Trend chart data (one dataset per metric) ─────────────────── */
  const trendData = useMemo(() => {
    const mk = () => ({ Day: new Map(), Week: new Map(), Month: new Map() });
    const activeUsers = mk(); // distinct users per bucket
    const totalLogins = mk(); // count of login-activity records per bucket
    const newAccounts = mk(); // count of locations created per bucket

    const keysOf = (d) => ({
      Day:   d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      Week:  `W${Math.ceil(d.getDate() / 7)}`,
      Month: d.toLocaleDateString(undefined, { month: "short" }),
    });

    loginActivity.forEach((la) => {
      if (!la.createdAt) return;
      const d = new Date(la.createdAt);
      if (isNaN(d)) return;
      const key = keysOf(d);
      const uid = la.userId ? String(la.userId) : la._id;
      ["Day", "Week", "Month"].forEach((p) => {
        const set = activeUsers[p].get(key[p]) || new Set();
        set.add(uid); activeUsers[p].set(key[p], set);
        totalLogins[p].set(key[p], (totalLogins[p].get(key[p]) || 0) + 1);
      });
    });

    locations.forEach((l) => {
      const created = l.createdAt || l.dateAdded;
      if (!created) return;
      const d = new Date(created);
      if (isNaN(d)) return;
      const key = keysOf(d);
      ["Day", "Week", "Month"].forEach((p) => {
        newAccounts[p].set(key[p], (newAccounts[p].get(key[p]) || 0) + 1);
      });
    });

    const toArr = (m, isSet) => {
      const cats = Array.from(m.keys());
      const vals = cats.map((c) => (isSet ? m.get(c).size : m.get(c)));
      return cats.length ? { cats, vals } : { cats: ["—"], vals: [0] };
    };
    const pack = (b, isSet) => ({
      Day: toArr(b.Day, isSet), Week: toArr(b.Week, isSet), Month: toArr(b.Month, isSet),
    });
    return {
      "Active Users": pack(activeUsers, true),
      "Total Logins": pack(totalLogins, false),
      "New Accounts": pack(newAccounts, false),
    };
  }, [loginActivity, locations]);

  const activeTrend = (trendData[metric] || trendData["Active Users"])[trendRange];
  const metricUnit = metric === "Active Users" ? "users" : metric === "Total Logins" ? "logins" : "accounts";

  const trendOptions = {
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#3b82f6"],
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0.01 } },
    legend: { show: false },
    xaxis: { categories: activeTrend.cats, labels: { style: { fontSize: "11px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: "11px" } }, min: 0 },
    grid: { borderColor: "#f3f4f6", strokeDashArray: 4 },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (v) => `${v} ${metricUnit}` } },
  };
  const trendSeries = [{ name: metric, data: activeTrend.vals }];

  /* ── Heatmap (day × hour) ──────────────────────────────────────── */
  const heatmap = useMemo(() => {
    const now = new Date();
    let cols, colOf, from;

    if (heatView === "month") {
      // Month view: one column per calendar date (1 → end of this month).
      const y = now.getFullYear();
      const mo = now.getMonth();
      const daysInMonth = new Date(y, mo + 1, 0).getDate();
      cols = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
      from = new Date(y, mo, 1);                  // 1st of this month
      colOf = (d) => d.getDate() - 1;             // date → column
    } else {
      // Week view: Mon → Sun of the current week.
      cols = DAYS;
      from = new Date(now);
      from.setHours(0, 0, 0, 0);
      from.setDate(from.getDate() - mondayIndex(from.getDay())); // back to Monday
      colOf = (d) => mondayIndex(d.getDay());
    }

    const grid = Array.from({ length: cols.length }, () => Array(24).fill(0));
    const fromTs = from.getTime();

    loginActivity.forEach((la) => {
      if (!la.createdAt) return;
      const d = new Date(la.createdAt);
      if (isNaN(d)) return;
      if (d.getTime() < fromTs) return;           // outside the selected period
      const ci = colOf(d);
      if (ci < 0 || ci >= grid.length) return;
      grid[ci][d.getHours()] += 1;
    });

    let maxCount = 0;
    grid.forEach((row) => row.forEach((c) => { if (c > maxCount) maxCount = c; }));
    return { grid, cols, maxCount };
  }, [loginActivity, heatView]);
  const totalUsersForHeat = users.length || 1;

  /* ── Sub-Accounts table rows ───────────────────────────────────── */
  const subAccountRows = useMemo(() => {
    return locations.map((l) => {
      const totalSeconds = sumTimesheetSeconds(l.timesheet);
      const act = locationActivity.get(String(l.id));
      const days = act?.lastLogin ? daysBetween(act.lastLogin) : Infinity;
      const status = days <= 7 ? "Active" : days <= 30 ? "At Risk" : "Dormant";
      const totalUsers = users.filter((u) => Array.isArray(u.ghl_locations) && u.ghl_locations.includes(l.id)).length;
      const activeUsers = act?.activeUsers30d?.size || 0;
      const name = l.name || l.loc_name || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "Unnamed";
      return {
        id: l.id,
        raw: l,
        name,
        initials: initialsOf(name),
        color: colorFor(l.id || name),
        plan: l.productId || "—",
        billing: l.subscriptionStatus === "active" ? "Active" : (l.subscriptionStatus || "—"),
        lastLogin: formatDateTime(act?.lastLogin),
        totalTime: formatDuration(totalSeconds),
        totalSeconds,
        activePct: totalUsers > 0 ? Math.min(100, Math.round((activeUsers / totalUsers) * 100)) : 0,
        activeOf: `${activeUsers} of ${totalUsers}`,
        status: status === "At Risk" ? "Low Activity" : status,
        feedbackRequested: !!l.feedbackRequested,
      };
    });
  }, [locations, users, locationActivity]);

  const filteredSubs = useMemo(
    () => subAccountRows.filter((a) => a.name.toLowerCase().includes(subSearch.toLowerCase())),
    [subAccountRows, subSearch]
  );

  const toggleFeedbackRequested = async (locationId, current) => {
    setTogglingId(locationId);
    const next = !current;
    setLocations((prev) => prev.map((l) => l.id === locationId ? { ...l, feedbackRequested: next } : l));
    const { response, error } = await AppService.webhook.toggle_feedback_requested({
      payload: { id: locationId, feedbackRequested: next },
    });
    if (!response?.success) {
      setLocations((prev) => prev.map((l) => l.id === locationId ? { ...l, feedbackRequested: current } : l));
      console.error("toggle failed", error);
    }
    setTogglingId(null);
  };

  /* ── Users table rows ──────────────────────────────────────────── */
  const userRows = useMemo(() => {
    return users.map((u) => {
      const totalSeconds = sumTimesheetSeconds(u.timesheet);
      const act = userActivity.get(String(u.id));
      const subId = act?.locationId || (Array.isArray(u.ghl_locations) ? u.ghl_locations[0] : null);
      const sub = subId ? locationById.get(String(subId)) : null;
      const subName = sub?.name || sub?.loc_name || (Array.isArray(u.location) && u.location[0]) || "—";
      const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username || u.email || "Unnamed";
      const freq = act ? frequencyLabel(act.loginDays.size) : "Ghosting";
      return {
        id: u.id,
        raw: u,
        name: fullName,
        email: u.email || "—",
        initials: initialsOf(fullName),
        color: colorFor(u.id || fullName),
        subAccount: subName,
        subInitials: initialsOf(subName),
        subColor: colorFor(subId || subName),
        role: (u.ghl_role || u.type || "User").replace(/^./, (c) => c.toUpperCase()),
        lastLogin: formatDateTime(act?.lastLogin),
        timeSpent: totalSeconds > 0 ? formatDuration(totalSeconds) : null,
        totalSeconds,
        freq,
      };
    });
  }, [users, userActivity, locationById]);

  const filteredUsers = useMemo(
    () => userRows.filter((u) =>
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearch.toLowerCase())
    ),
    [userRows, userSearch]
  );

  const tabs = [
    { key: "overview",    label: "Overview",     icon: "solar:chart-2-outline" },
    { key: "subaccounts", label: "Sub-Accounts", icon: "solar:buildings-2-outline" },
    { key: "users",       label: "Users",        icon: "solar:users-group-rounded-outline" },
  ];

  const statCards = [
    { label: "Total Accounts",   sub: null,                                       value: overviewStats.totalAccounts, icon: "solar:buildings-2-outline",        bg: "#eff6ff", ic: "#2563eb" },
    { label: "Active Accounts",  sub: "At least one user login in last 7 days",   value: overviewStats.active,        icon: "solar:user-check-rounded-outline", bg: "#f0fdf4", ic: "#16a34a" },
    { label: "At Risk Accounts", sub: "No user login for over 7 days",            value: overviewStats.atRisk,        icon: "solar:danger-triangle-outline",    bg: "#fef9c3", ic: "#d97706" },
    { label: "Dormant Accounts", sub: "No user login for over 30 days",           value: overviewStats.dormant,       icon: "solar:user-cross-rounded-outline", bg: "#fef2f2", ic: "#dc2626", note: "Historical churn candidates" },
  ];

  if (selectedAccount) {
    return (
      <SubAccountDetail
        account={selectedAccount}
        users={users}
        loginActivity={loginActivity}
        feedback={feedback}
        onBack={() => setSelectedAccount(null)}
      />
    );
  }

  return (
    <div className="container-fluid p-24">
      <p className="text-secondary-light text-sm mb-16">Monitor whether your customers are logging in to their accounts</p>

      {/* Tabs */}
      <div className="mb-24" style={{ borderBottom: "2px solid var(--border-color)" }}>
        <div className="d-flex gap-0">
          {tabs.map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: "10px 20px", background: "none", border: "none", borderBottom: activeTab === tab.key ? "2px solid var(--primary-600)" : "2px solid transparent", marginBottom: -2, color: activeTab === tab.key ? "var(--primary-600)" : "var(--text-secondary-light)", fontWeight: activeTab === tab.key ? 600 : 400, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "color 0.15s" }}>
              <Icon icon={tab.icon} style={{ fontSize: 15 }} />{tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-center py-32 text-secondary-light text-sm">Loading login activity…</div>
      )}

      {!loading && activeTab === "overview" && (
        <>
          {/* Stat cards */}
          <div className="row gy-3 mb-24">
            {statCards.map((s, i) => (
              <div className="col-xl-3 col-sm-6" key={i}>
                <div className="card h-100">
                  <div className="card-body p-20 d-flex align-items-start justify-content-between gap-8">
                    <div>
                      <p className="fw-semibold text-sm mb-2">{s.label}</p>
                      {s.sub && <p className="text-secondary-light mb-8" style={{ fontSize: 11 }}>{s.sub}</p>}
                      <span className="fw-bold" style={{ fontSize: 28 }}>{s.value}</span>
                      {s.note && <p className="mb-0 mt-4" style={{ fontSize: 11, color: "#dc2626" }}>{s.note}</p>}
                    </div>
                    <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                      style={{ width: 40, height: 40, background: s.bg }}>
                      <Icon icon={s.icon} style={{ fontSize: 20, color: s.ic }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Login Frequency Bar Chart */}
          <div className="card mb-24">
            <div className="card-body p-24">
              <h6 className="fw-semibold mb-2 d-flex align-items-center gap-8">
                Accounts by Login Frequency
                <Icon icon="solar:info-circle-outline" style={{ fontSize: 14, opacity: 0.5 }} />
              </h6>
              <p className="text-secondary-light text-xs mb-24">Distribution of accounts based on active login days (last 30 days)</p>

              <div style={{ display: "flex", alignItems: "flex-end", gap: 32, height: 180, padding: "0 24px" }}>
                {freqBars.map((b, i) => {
                  const barH = maxFreq > 0 ? Math.max((b.count / maxFreq) * 140, b.count > 0 ? 20 : 0) : 0;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, color: "#6b7280", fontWeight: b.count > 0 ? 600 : 400 }}>{b.count}<br/>Accounts</span>
                      <div style={{ width: "60%", height: barH || 4, background: b.count > 0 ? b.color : "#e5e7eb", borderRadius: "4px 4px 0 0", transition: "height 0.3s" }} />
                      <span style={{ fontSize: 12, color: "#374151", textAlign: "center" }}>{b.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="d-flex flex-wrap gap-20 mt-24 justify-content-center">
                {freqBars.map((b, i) => (
                  <span key={i} className="d-flex align-items-center gap-6" style={{ fontSize: 11, color: "#6b7280" }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: b.color, display: "inline-block" }} />
                    {b.label} ({b.sub} logged in)
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Analytics Trends */}
          <div className="card mb-24">
            <div className="card-body p-24">
              <p className="text-xs fw-semibold text-secondary-light mb-12" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>Analytics Trends</p>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-20">
                <div className="d-flex align-items-center gap-12">
                  <span className="text-sm text-secondary-light">Metric:</span>
                  <select className="form-select form-select-sm radius-8" style={{ width: 150 }}
                    value={metric} onChange={(e) => setMetric(e.target.value)}>
                    <option>Active Users</option>
                    <option>Total Logins</option>
                    <option>New Accounts</option>
                  </select>
                </div>
                <div className="d-flex align-items-center gap-12">
                  <label className="d-flex align-items-center gap-6 text-sm text-secondary-light" style={{ cursor: "pointer" }}>
                    <input type="checkbox" checked={compareMode} onChange={(e) => setCompareMode(e.target.checked)}
                      style={{ accentColor: "var(--primary-600)" }} />
                    Compare to previous period
                  </label>
                  <div className="d-flex" style={{ border: "1px solid var(--border-color)", borderRadius: 8, overflow: "hidden" }}>
                    {["Day","Week","Month"].map((r) => (
                      <button key={r} onClick={() => setTrendRange(r)}
                        style={{ padding: "5px 14px", border: "none", background: trendRange === r ? "var(--primary-600)" : "transparent", color: trendRange === r ? "#fff" : "var(--text-secondary-light)", fontSize: 13, fontWeight: trendRange === r ? 600 : 400, cursor: "pointer" }}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <ReactApexChart options={trendOptions} series={trendSeries} type="area" height={240} />
              <div className="d-flex justify-content-center mt-8">
                <span className="d-flex align-items-center gap-6 text-xs text-secondary-light">
                  <span style={{ width: 24, height: 2, background: "#3b82f6", display: "inline-block", borderRadius: 2 }} />
                  {metric}
                </span>
              </div>
            </div>
          </div>

          {/* Hourly Activity Heatmap */}
          <div className="card">
            <div className="card-body p-24">
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-12 mb-8">
                <div>
                  <h6 className="fw-semibold mb-2">Hourly Activity Heatmap</h6>
                  <p className="text-secondary-light text-xs mb-0">
                    Heatmap of login frequency by hour. Use this to track utilization rates and identify core working hours.
                  </p>
                </div>
                <div className="d-flex align-items-center gap-8">
                  <span className="text-sm text-secondary-light" style={{ opacity: heatView === "week" ? 1 : 0.4 }}>Week View</span>
                  <div onClick={() => setHeatView(heatView === "week" ? "month" : "week")}
                    style={{ width: 40, height: 22, borderRadius: 11, background: heatView === "month" ? "var(--primary-600)" : "#d1d5db", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                    <div style={{ position: "absolute", top: 3, left: heatView === "month" ? 21 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                  </div>
                  <span className="text-sm text-secondary-light" style={{ opacity: heatView === "month" ? 1 : 0.4 }}>Month View</span>
                </div>
              </div>

              <div style={{ overflowX: "auto", marginTop: 16 }}>
                <table style={{ borderCollapse: "separate", borderSpacing: 2, minWidth: 52 + heatmap.cols.length * (heatView === "month" ? 26 : 70) }}>
                  <thead>
                    <tr>
                      <th style={{ width: 52, fontSize: 11, color: "#9ca3af", fontWeight: 600, paddingRight: 8, textAlign: "left" }}>Hour</th>
                      {heatmap.cols.map((d) => (
                        <th key={d} style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textAlign: "center", paddingBottom: 6, minWidth: heatView === "month" ? 24 : 68 }}>{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {HOURS.map((hour, hi) => (
                      <tr key={hi}>
                        <td style={{ fontSize: 11, color: "#9ca3af", paddingRight: 8, whiteSpace: "nowrap", verticalAlign: "middle" }}>{hour}</td>
                        {heatmap.cols.map((day, di) => {
                          const count = heatmap.grid[di][hi];
                          const pct = heatmap.maxCount > 0 ? Math.round((count / heatmap.maxCount) * 100) : 0;
                          const level = heatLevelFromPct(pct);
                          const isHovered = tooltip?.hi === hi && tooltip?.di === di;
                          return (
                            <td key={di} style={{ position: "relative" }}>
                              <div
                                onMouseEnter={() => setTooltip({ hi, di, hour, day, level, pct, count })}
                                onMouseLeave={() => setTooltip(null)}
                                style={{ width: "100%", height: 22, borderRadius: 3, background: HEAT_COLORS[level], cursor: "default", border: isHovered ? "1px solid #3b82f6" : "1px solid transparent", transition: "border 0.1s" }}
                              />
                              {isHovered && (
                                <div style={{ position: "absolute", bottom: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)", background: "#1f2937", color: "#fff", borderRadius: 6, padding: "6px 10px", fontSize: 11, whiteSpace: "nowrap", zIndex: 99999, pointerEvents: "none", lineHeight: 1.6 }}>
                                  <div className="fw-semibold">{day}, {hour}</div>
                                  <div style={{ color: HEAT_COLORS[Math.max(level, 1)] }}>{HEAT_LABELS[level]} ({pct}%)</div>
                                  <div>{count} logins</div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex align-items-center gap-16 mt-16 flex-wrap justify-content-center">
                <span className="text-xs text-secondary-light">Utilization:</span>
                {HEAT_LABELS.map((lbl, i) => (
                  <span key={i} className="d-flex align-items-center gap-5 text-xs text-secondary-light">
                    <span style={{ width: 14, height: 14, borderRadius: 2, background: HEAT_COLORS[i], display: "inline-block", border: "1px solid #e5e7eb" }} />
                    {lbl}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Sub-Accounts Tab */}
      {!loading && activeTab === "subaccounts" && (
        <>
          <div className="d-flex align-items-center gap-12 mb-16 flex-wrap">
            <div className="flex-grow-1 position-relative" style={{ maxWidth: 420 }}>
              <Icon icon="solar:magnifer-outline" style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:16, color:"#9ca3af" }} />
              <input className="form-control form-control-sm radius-8" style={{ paddingLeft:36 }}
                placeholder="Search accounts by name"
                value={subSearch} onChange={(e) => setSubSearch(e.target.value)} />
            </div>
            <div className="ms-auto d-flex gap-10">
              <button className="btn btn-sm d-flex align-items-center gap-6 radius-8 px-14 py-6" style={{ border:"1px solid var(--border-color)", background:"#fff", fontSize:13 }}>
                <Icon icon="solar:tuning-2-outline" style={{ fontSize:15 }} /> Columns
              </button>
              <button className="btn btn-sm d-flex align-items-center gap-6 radius-8 px-14 py-6" style={{ border:"1px solid var(--border-color)", background:"#fff", fontSize:13 }}>
                <Icon icon="solar:export-outline" style={{ fontSize:15 }} /> Export CSV
              </button>
            </div>
          </div>
          <div className="card" style={{ overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
                <thead>
                  <tr style={{ background:"#fafafa", borderBottom:"1px solid var(--border-color)" }}>
                    <th style={{ width:36, padding:"10px 16px" }}><input type="checkbox" style={{ accentColor:"var(--primary-600)" }} /></th>
                    {["Account","SaaS Plan","Last Login","Total Time","Active Users","Login Activity Status","Feedback"].map((label) => (
                      <th key={label} style={{ padding:"10px 12px", fontSize:11, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.04em" }}>{label}</th>
                    ))}
                  </tr>
                  <tr style={{ borderBottom:"1px solid var(--border-color)", background:"#fff" }}>
                    <td colSpan={8} style={{ padding:"8px 16px" }}>
                      <span style={{ fontWeight:600, fontSize:13 }}>Sub-Accounts</span>
                      <span style={{ marginLeft:8, background:"#f3f4f6", borderRadius:12, padding:"2px 8px", fontSize:12, fontWeight:600 }}>{filteredSubs.length}</span>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-32 text-secondary-light text-sm">No sub-accounts found</td></tr>
                  )}
                  {filteredSubs.map((acc, i) => {
                    const fs = FREQ_STYLE[acc.status] || FREQ_STYLE["Ghosting"];
                    return (
                      <tr key={acc.id || i} style={{ borderBottom:"1px solid var(--border-color)" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fafbff"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{ padding:"12px 16px" }}><input type="checkbox" style={{ accentColor:"var(--primary-600)" }} /></td>
                        <td style={{ padding:"12px 12px", minWidth:180 }}>
                          <div className="d-flex align-items-center gap-10">
                            <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                              style={{ width:32, height:32, background:acc.color+"22", color:acc.color, fontSize:12 }}>
                              {acc.initials}
                            </div>
                            <span style={{ fontWeight:500, fontSize:13, color:"var(--primary-600)", cursor:"pointer" }}
                              onClick={() => setSelectedAccount(acc)}>{acc.name}</span>
                          </div>
                        </td>
                        <td style={{ padding:"12px 12px", minWidth:160 }}>
                          <div style={{ fontSize:13 }}>{acc.plan}</div>
                          <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{acc.billing}</div>
                        </td>
                        <td style={{ padding:"12px 12px", fontSize:13, color: acc.lastLogin ? "#374151" : "#9ca3af", whiteSpace:"nowrap" }}>{acc.lastLogin || "Never"}</td>
                        <td style={{ padding:"12px 12px" }}>
                          <div className="d-flex align-items-center gap-6">
                            <Icon icon="solar:clock-circle-outline" style={{ fontSize:14, color:"#9ca3af" }} />
                            <span style={{ fontSize:13 }}>{acc.totalTime}</span>
                          </div>
                        </td>
                        <td style={{ padding:"12px 12px" }}>
                          <div style={{ fontSize:14, fontWeight:700, color: acc.activePct === 100 ? "#16a34a" : acc.activePct > 0 ? "#d97706" : "#dc2626" }}>{acc.activePct}%</div>
                          <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{acc.activeOf}</div>
                        </td>
                        <td style={{ padding:"12px 12px" }}>
                          <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20, background:fs.bg, color:fs.color, fontSize:12, fontWeight:600 }}>
                            <span style={{ width:7, height:7, borderRadius:"50%", background:fs.dot, display:"inline-block" }} />
                            {acc.status}
                          </span>
                        </td>
                        <td style={{ padding:"12px 12px" }}>
                          <div
                            onClick={() => togglingId !== acc.id && toggleFeedbackRequested(acc.id, acc.feedbackRequested)}
                            role="switch"
                            aria-checked={acc.feedbackRequested}
                            title={acc.feedbackRequested ? "Feedback requested" : "Request feedback"}
                            style={{
                              width: 36, height: 20, borderRadius: 10,
                              background: acc.feedbackRequested ? "#16a34a" : "#d1d5db",
                              cursor: togglingId === acc.id ? "wait" : "pointer",
                              position: "relative", transition: "background 0.2s",
                              opacity: togglingId === acc.id ? 0.6 : 1,
                            }}>
                            <div style={{
                              position: "absolute", top: 2,
                              left: acc.feedbackRequested ? 18 : 2,
                              width: 16, height: 16, borderRadius: "50%",
                              background: "#fff", transition: "left 0.2s",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                            }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Users Tab */}
      {!loading && activeTab === "users" && (
        <>
          <div className="d-flex align-items-center gap-12 mb-16 flex-wrap">
            <div className="flex-grow-1 position-relative" style={{ maxWidth:420 }}>
              <Icon icon="solar:magnifer-outline" style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:16, color:"#9ca3af" }} />
              <input className="form-control form-control-sm radius-8" style={{ paddingLeft:36 }}
                placeholder="Search users by name or email"
                value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
            </div>
            <div className="ms-auto">
              <button className="btn btn-sm d-flex align-items-center gap-6 radius-8 px-14 py-6" style={{ border:"1px solid var(--border-color)", background:"#fff", fontSize:13 }}>
                <Icon icon="solar:export-outline" style={{ fontSize:15 }} /> Export CSV
              </button>
            </div>
          </div>
          <div className="card" style={{ overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
                <thead>
                  <tr style={{ background:"#fafafa", borderBottom:"1px solid var(--border-color)" }}>
                    <th style={{ width:36, padding:"10px 16px" }}><input type="checkbox" style={{ accentColor:"var(--primary-600)" }} /></th>
                    {["User","Sub-Account","Role","Last Login","Time Spent","Login Frequency"].map((label) => (
                      <th key={label} style={{ padding:"10px 12px", fontSize:11, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.04em", whiteSpace:"nowrap" }}>{label}</th>
                    ))}
                  </tr>
                  <tr style={{ borderBottom:"1px solid var(--border-color)", background:"#fff" }}>
                    <td colSpan={7} style={{ padding:"8px 16px" }}>
                      <span style={{ fontWeight:600, fontSize:13 }}>Users</span>
                      <span style={{ marginLeft:8, background:"#f3f4f6", borderRadius:12, padding:"2px 8px", fontSize:12, fontWeight:600 }}>{filteredUsers.length}</span>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-32 text-secondary-light text-sm">No users found</td></tr>
                  )}
                  {filteredUsers.map((u, i) => {
                    const fs = FREQ_STYLE[u.freq] || FREQ_STYLE["Ghosting"];
                    const isAdmin = /admin/i.test(u.role);
                    const roleStyle = isAdmin
                      ? { bg:"#f0fdf4", color:"#15803d" }
                      : { bg:"#eff6ff", color:"#2563eb" };
                    return (
                      <tr key={u.id || i} style={{ borderBottom:"1px solid var(--border-color)" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fafbff"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{ padding:"12px 16px" }}><input type="checkbox" style={{ accentColor:"var(--primary-600)" }} /></td>
                        <td style={{ padding:"12px 12px", minWidth:200 }}>
                          <div className="d-flex align-items-center gap-10">
                            <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                              style={{ width:34, height:34, background:u.color+"22", color:u.color, fontSize:12 }}>
                              {u.initials}
                            </div>
                            <div>
                              <div style={{ fontWeight:500, fontSize:13 }}>{u.name}</div>
                              <div style={{ fontSize:11, color:"#9ca3af" }}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding:"12px 12px", minWidth:180 }}>
                          <div className="d-flex align-items-center gap-8">
                            <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                              style={{ width:26, height:26, background:u.subColor+"22", color:u.subColor, fontSize:11 }}>
                              {u.subInitials}
                            </div>
                            <span style={{ fontSize:13 }}>{u.subAccount}</span>
                          </div>
                        </td>
                        <td style={{ padding:"12px 12px" }}>
                          <span style={{ padding:"3px 10px", borderRadius:20, background:roleStyle.bg, color:roleStyle.color, fontSize:12, fontWeight:600 }}>
                            {u.role}
                          </span>
                        </td>
                        <td style={{ padding:"12px 12px", fontSize:13, color: u.lastLogin ? "#374151" : "#9ca3af", whiteSpace:"nowrap" }}>
                          {u.lastLogin || "Never"}
                        </td>
                        <td style={{ padding:"12px 12px" }}>
                          {u.timeSpent ? (
                            <div className="d-flex align-items-center gap-6">
                              <span style={{ fontSize:13 }}>{u.timeSpent}</span>
                              <Icon icon="solar:clock-circle-outline" style={{ fontSize:13, color:"#9ca3af" }} />
                            </div>
                          ) : <span style={{ fontSize:13, color:"#9ca3af" }}>-</span>}
                        </td>
                        <td style={{ padding:"12px 12px" }}>
                          <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20, background:fs.bg, color:fs.color, fontSize:12, fontWeight:600 }}>
                            <span style={{ width:7, height:7, borderRadius:"50%", background:fs.dot, display:"inline-block" }} />
                            {u.freq}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LoginActivity;
